import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

export function getStorage(key: string): string | null {
  return localStorage.getItem(key);
}

export function setStorage(key: string, value: string): void {
  localStorage.setItem(key, value);
}

// ==================== TESTS ====================

import * as fc from "fast-check";

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest;

  describe("cn", () => {
    it("merges basic classes", () => {
      expect(cn("foo", "bar")).toBe("foo bar");
    });

    it("handles conditional classes", () => {
      const getClassName = (includeBar: boolean) =>
        cn("foo", includeBar && "bar", "baz");
      expect(getClassName(false)).toBe("foo baz");
    });

    it("handles undefined and null", () => {
      expect(cn("foo", undefined, null, "bar")).toBe("foo bar");
    });

    it("fuzz: never throws on any input", () => {
      fc.assert(
        fc.property(fc.anything(), (input: unknown) => {
          expect(() => cn(input as ClassValue)).not.toThrow();
        }),
        { numRuns: 100 },
      );
    });
  });

  describe("cookie", () => {
    it("parses existing cookie", () => {
      Object.defineProperty(document, "cookie", {
        configurable: true,
        value: "test=value; other=thing",
      });
      expect(getCookie("test")).toBe("value");
    });

    it("returns null for missing cookie", () => {
      Object.defineProperty(document, "cookie", {
        configurable: true,
        value: "other=value",
      });
      expect(getCookie("missing")).toBeNull();
    });

    it("decodes URL-encoded values", () => {
      Object.defineProperty(document, "cookie", {
        configurable: true,
        value: "test=hello%20world",
      });
      expect(getCookie("test")).toBe("hello world");
    });
  });
}
